/*
 * THESIS: a showroom shelf, honest about direction of travel: Benjamin is a
 *   door out, Maestro is shown working here; refuses the two-column pricing
 *   comparison this category ships.
 * OWN-WORLD: Maestro surfaces, cyan owns Benjamin's panel, the pyramid scale
 *   gradient CTA stays the single gradient on the page.
 * STORY: a buyer or partner understands both products in one scroll, sees the
 *   loop line binding them, and the partner band opens the channel door.
 * FIRST VIEWPORT: kicker, positioning headline, the two platform panels
 *   beginning immediately beneath.
 * FORM: shelf of two deep panels + loop ribbon + partner band; pinned
 *   direction (brief), no seed.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Seo from '../Seo'
import SiteAmbient from '../common/SiteAmbient'
import trackEvent from '../../utils/trackEvent'
import { getCtaContent } from '../../content/ctaContent'
import { getPlatformsContent } from '../../content/platformsContent'

const TALK_PATH = '/talk?i=partners'

function useReveal(threshold = 0.1) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-visible')
      return undefined
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible')
          obs.disconnect()
        }
      },
      { threshold }
    )
    obs.observe(el)
    // failsafe: if the observer never fires (hidden tab, odd engine states),
    // the section still reveals instead of holding opacity 0 forever
    const failsafe = setTimeout(() => { el.classList.add('is-visible'); obs.disconnect() }, 2600)
    return () => { obs.disconnect(); clearTimeout(failsafe) }
  }, [threshold])
  return ref
}

const PlatformsPage = () => {
  const { i18n } = useTranslation()
  const c = getPlatformsContent(i18n.language)
  const bookLabel = getCtaContent(i18n.language).close.book
  const heroRef = useReveal(0.05)

  return (
    <div className="relative isolate bg-transparent font-Quicksand text-white">
      <SiteAmbient />
      <Seo title={c.seo.title} description={c.seo.description} path="/platforms" />

      <header className="px-6 pb-10 pt-32 md:px-16 md:pt-40">
        <div ref={heroRef} className="reveal-on-scroll mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/75">{c.hero.kicker}</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-light leading-[1.05] md:text-6xl">{c.hero.headline}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">{c.hero.body}</p>
        </div>
      </header>

      <main className="px-6 pb-24 md:px-16">
        <div className="mx-auto max-w-6xl space-y-14">
          {/* Benjamin */}
          <section id="benjamin" className="hover-lift scroll-mt-28 rounded-2xl border border-pyramid-19/25 bg-white/[0.02] p-7 md:p-10 hover:border-pyramid-19/45">
            <p className="text-xs uppercase tracking-[0.35em] text-pyramid-19">{c.benjamin.kicker}</p>
            <h2 className="mt-3 max-w-2xl text-2xl font-light md:text-[2rem]">{c.benjamin.title}</h2>
            <p className="mt-3 max-w-3xl leading-7 text-slate-300">{c.benjamin.body}</p>
            <ul className="mt-6 space-y-2.5">
              {c.benjamin.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm leading-6 text-slate-300">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pyramid-19" />
                  {p}
                </li>
              ))}
            </ul>
            <a
              href={c.benjamin.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('platforms_benjamin_click')}
              className="mt-8 inline-flex items-center gap-2 rounded-[10px] border border-pyramid-19/40 bg-pyramid-19/10 px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pyramid-19/20"
            >
              {c.benjamin.cta}
              <span aria-hidden="true">&#8599;</span>
            </a>
          </section>

          {/* Maestro */}
          <section id="maestro" className="hover-lift scroll-mt-28 rounded-2xl border border-white/10 bg-white/[0.02] p-7 md:p-10 hover:border-pyramid-1/40">
            <p className="text-xs uppercase tracking-[0.35em] text-pyramid-1">{c.maestro.kicker}</p>
            <h2 className="mt-3 max-w-2xl text-2xl font-light md:text-[2rem]">{c.maestro.title}</h2>
            <p className="mt-3 max-w-3xl leading-7 text-slate-300">{c.maestro.body}</p>
            <ul className="mt-6 space-y-2.5">
              {c.maestro.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm leading-6 text-slate-300">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pyramid-1" />
                  {p}
                </li>
              ))}
            </ul>
            <figure className="mt-8">
              <div className="max-w-full overflow-x-auto">
                <div className="overflow-hidden rounded-xl bg-[#1a212a] ring-1 ring-white/[0.12] shadow-2xl shadow-black/50">
                  <img src={c.maestro.shot.src} alt={c.maestro.shot.alt} loading="lazy" className="block w-full" />
                </div>
              </div>
              <figcaption className="mt-3 text-xs leading-5 text-slate-400">{c.maestro.shot.caption}</figcaption>
            </figure>
            <p className="mt-6 text-sm leading-6 text-pyramid-1/90">{c.maestro.note}</p>
            <a
              href={c.maestro.href}
              onClick={() => trackEvent('platforms_maestro_click')}
              className="mt-7 inline-flex items-center gap-2 rounded-[10px] border border-pyramid-1/40 bg-pyramid-1/10 px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pyramid-1/20"
            >
              {c.maestro.cta}
              <span aria-hidden="true">&#8594;</span>
            </a>
          </section>

          {/* loop ribbon */}
          <section className="rounded-2xl border border-pyramid-10/25 bg-pyramid-10/[0.06] p-7 md:flex md:items-center md:justify-between md:gap-8 md:p-9">
            <p className="max-w-2xl text-lg font-light leading-8 text-white">{c.loopLine.text}</p>
            <a href={c.loopLine.href} className="btn-board btn-board-orange mt-5 shrink-0 md:mt-0">
              {c.loopLine.cta}
              <span aria-hidden="true">&#8594;</span>
            </a>
          </section>

          {/* partners */}
          <section id="partners" className="scroll-mt-28 rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center md:p-12">
            <p className="text-xs uppercase tracking-[0.35em] text-pyramid-1/75">{c.partners.kicker}</p>
            <h2 className="mt-3 text-2xl font-light md:text-3xl">{c.partners.title}</h2>
            <p className="mx-auto mt-3 max-w-2xl leading-7 text-slate-400">{c.partners.body}</p>
            <ul className="mx-auto mt-6 flex max-w-3xl flex-col items-center gap-2.5">
              {c.partners.points.map((pt) => (
                <li key={pt} className="flex items-start gap-3 text-sm leading-6 text-slate-300">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pyramid-10" />
                  {pt}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={TALK_PATH}
                onClick={() => trackEvent('platforms_partner_click')}
                className="inline-flex items-center gap-2 rounded-[10px] bg-gradient-to-r from-pyramid-1 via-pyramid-10 to-pyramid-19 px-7 py-3 font-semibold text-ebony shadow-lg shadow-pyramid-10/30 transition-all hover:-translate-y-0.5"
              >
                {c.partners.cta}
                <span aria-hidden="true">&#8594;</span>
              </a>
              <a
              href="/book"
              onClick={() => trackEvent('platforms_book_click')}
              className="inline-flex items-center gap-2 rounded-[10px] border border-pyramid-19/50 px-7 py-3 font-semibold text-pyramid-19 transition-all hover:bg-pyramid-19/10"
              >
              {bookLabel}
              <span aria-hidden="true">&#8594;</span>
              </a>
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}

export default PlatformsPage
